import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    downloadGalleryImagesAsZip,
    fetchGalleryImagesForDownload,
    probeGalleryTotalBytes,
} from '../utils/galleryDownload';

/**
 * @param {{
 *   authToken: string | null;
 *   useLiveApi: boolean;
 * }} options
 */
export function useGalleryDownload({ authToken, useLiveApi }) {
    const { t } = useTranslation();
    const [downloadModal, setDownloadModal] = useState(
        /** @type {null | {
         *   gallery: import('../data/galleries').GalleryAlbum,
         *   phase: 'loading' | 'confirm' | 'downloading',
         *   downloadStep?: 'fetching' | 'packing',
         *   fileCount: number,
         *   totalBytes: number,
         *   images: Array<{ id: string, src: string, alt?: string }>,
         *   error: string | null,
         * }} */ (null),
    );

    const closeDownloadModal = useCallback(() => {
        setDownloadModal(null);
    }, []);

    const openGalleryDownload = useCallback(
        async (
            gallery,
            preloadedImages = null,
        ) => {
            if (!useLiveApi || !authToken) {
                toast.info(t('galleriesPage.downloadRequiresLiveApi'));
                return;
            }

            setDownloadModal({
                gallery,
                phase: 'loading',
                fileCount: 0,
                totalBytes: 0,
                images: [],
                error: null,
            });

            try {
                const images =
                    preloadedImages ??
                    (await fetchGalleryImagesForDownload(
                        authToken,
                        gallery,
                    ));

                if (images.length === 0) {
                    setDownloadModal({
                        gallery,
                        phase: 'confirm',
                        fileCount: 0,
                        totalBytes: 0,
                        images: [],
                        error: t('galleriesPage.downloadNoImages'),
                    });
                    return;
                }

                const totalBytes = await probeGalleryTotalBytes(
                    images.map((img) => img.src),
                    authToken,
                );

                setDownloadModal({
                    gallery,
                    phase: 'confirm',
                    fileCount: images.length,
                    totalBytes,
                    images,
                    error: null,
                });
            } catch (err) {
                const msg =
                    err instanceof Error
                        ? err.message
                        : t('galleriesPage.downloadError');
                setDownloadModal({
                    gallery,
                    phase: 'confirm',
                    fileCount: 0,
                    totalBytes: 0,
                    images: [],
                    error: msg,
                });
            }
        },
        [useLiveApi, authToken, t],
    );

    const handleDownloadConfirm = useCallback(async () => {
        if (
            !downloadModal ||
            !authToken ||
            downloadModal.images.length === 0
        ) {
            return;
        }

        setDownloadModal((prev) =>
            prev
                ? {
                      ...prev,
                      phase: 'downloading',
                      downloadStep: 'fetching',
                      error: null,
                  }
                : prev,
        );

        try {
            await downloadGalleryImagesAsZip(
                downloadModal.images,
                downloadModal.gallery.title,
                authToken,
                {
                    onZipProgress: () => {
                        setDownloadModal((prev) =>
                            prev
                                ? { ...prev, downloadStep: 'packing' }
                                : prev,
                        );
                    },
                },
            );
            toast.success(t('galleriesPage.downloadSuccess'));
            setDownloadModal(null);
        } catch (err) {
            const msg =
                err instanceof Error
                    ? err.message
                    : t('galleriesPage.downloadError');
            setDownloadModal((prev) =>
                prev
                    ? { ...prev, phase: 'confirm', error: msg }
                    : prev,
            );
        }
    }, [downloadModal, authToken, t]);

    return {
        downloadModal,
        closeDownloadModal,
        openGalleryDownload,
        handleDownloadConfirm,
    };
}
